/* eslint-disable camelcase */
import { useEffect, useMemo, useRef, useState } from 'react'

import styles from './BillingInfoModal.module.scss'

import { BusinessVariant, getCheckoutSessionInfo } from '@/api/billing'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import PurchaseTerms from '@/components/PurchaseTerms'
import { useAnalytics } from '@/hooks/useAnalytics'
import { usePricing } from '@/hooks/usePricing'
import { PaymentSchedule, PlanType } from '@/types'
import { EventName, PLATFORM_ADMIN_PORTAL } from '@/utils/eventDefinitions'

interface BillingInfoModalProps {
  isOpen: boolean
  onClose: () => void
  defaultSchedule?: PaymentSchedule
  planType?: PlanType
  /** Self-serve Business option; when set the checkout and displayed prices use the new Business prices. */
  variant?: BusinessVariant
}

type CheckoutInfo = {
  url: string | null
  seatsCount: number
  trialDays: number
}

const PLAN_REF: Record<PlanType, Record<PaymentSchedule, string>> = {
  [PlanType.Pro]: {
    [PaymentSchedule.Monthly]: 'FLOW_PRO_MONTHLY',
    [PaymentSchedule.Annual]: 'FLOW_PRO_YEARLY',
  },
  [PlanType.Enterprise]: {
    [PaymentSchedule.Monthly]: 'FLOW_BUSINESS_MONTHLY',
    [PaymentSchedule.Annual]: 'FLOW_BUSINESS_YEARLY',
  },
  [PlanType.Team]: {
    [PaymentSchedule.Monthly]: 'FLOW_PRO_MONTHLY',
    [PaymentSchedule.Annual]: 'FLOW_PRO_YEARLY',
  },
}

export const BillingInfoModal = ({
  isOpen,
  onClose,
  defaultSchedule = PaymentSchedule.Annual,
  planType = PlanType.Pro,
  variant,
}: BillingInfoModalProps) => {
  const [selectedSchedule, setSelectedSchedule] = useState<PaymentSchedule>(defaultSchedule)
  const [wasOpen, setWasOpen] = useState<boolean>(isOpen)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [checkoutInfo, setCheckoutInfo] = useState<CheckoutInfo>({ url: null, seatsCount: 0, trialDays: 0 })
  const {
    getPriceForPlan,
    getBusinessOptionPrice,
    formatPrice,
    currency,
    isInitialized: isPricingInitialized,
  } = usePricing()
  const { trackEvent } = useAnalytics()

  // The modal stays mounted while closed, so the opener's schedule has to be re-applied on
  // open — otherwise checkout bills the cycle that was current at first mount. Syncing during
  // render (not in an effect) means the fetch below sees the synced cycle on its first run:
  // an effect would fetch once with the stale cycle and again after the sync, and Continue to
  // Payment could land on the stale cycle's Stripe session.
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen)
    if (isOpen) setSelectedSchedule(defaultSchedule)
  }

  const plan = useMemo(() => PLAN_REF[planType][selectedSchedule], [planType, selectedSchedule])

  const latestRequest = useRef(0)

  const fetchInfo = async () => {
    const requestToken = ++latestRequest.current
    setIsLoading(true)
    setError(null)
    try {
      const data = await getCheckoutSessionInfo({ plan, seats: 1, add_billing_info: true, currency, variant })
      if (requestToken !== latestRequest.current) return
      // Ensure returned data has the required fields
      if (!('data' in data) || !data.data?.url) {
        setError('Unable to load billing info. Please try again.')
        setCheckoutInfo({ url: null, seatsCount: 0, trialDays: 0 })
      } else {
        setCheckoutInfo({
          url: data.data.url ?? null,
          seatsCount: data.data.seats_charged ?? 0,
          trialDays: data.data.trial_period_days ?? 0,
        })
      }
    } catch {
      if (requestToken !== latestRequest.current) return
      setError('Unable to load billing info. Please try again.')
      setCheckoutInfo({ url: null, seatsCount: 0, trialDays: 0 })
    } finally {
      if (requestToken === latestRequest.current) setIsLoading(false)
    }
  }

  // Track modal open event once when modal opens (not on plan/schedule changes)
  // Wait for pricing to initialize to avoid race condition with undefined currency
  useEffect(() => {
    if (!isOpen || !isPricingInitialized) return
    trackEvent?.(EventName.TeamBillingModalOpened, { source: 'add_billing_info', platform: PLATFORM_ADMIN_PORTAL })
  }, [isOpen, isPricingInitialized, trackEvent])

  // Fetch checkout info when modal opens or plan/currency/variant changes
  useEffect(() => {
    if (!isOpen || !isPricingInitialized) return
    fetchInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plan, currency, variant, isPricingInitialized])

  const seatsCount = checkoutInfo.seatsCount

  // Get dynamic price from pricing API. A Business option prices from the new
  // Business fields so the displayed subtotal matches what checkout charges.
  const monthlyUnitPrice = variant
    ? getBusinessOptionPrice(variant, PaymentSchedule.Monthly)
    : getPriceForPlan(planType, PaymentSchedule.Monthly)
  const annualUnitPriceTotal = variant
    ? getBusinessOptionPrice(variant, PaymentSchedule.Annual)
    : getPriceForPlan(planType, PaymentSchedule.Annual)

  // For annual plans, we show the total annual price per seat
  // For monthly plans, we show the monthly price per seat
  const unitPrice = selectedSchedule === PaymentSchedule.Annual ? annualUnitPriceTotal : monthlyUnitPrice

  const subtotal = unitPrice !== undefined ? seatsCount * unitPrice : undefined
  const billedToday = checkoutInfo.trialDays === 0 ? (subtotal ?? 0) : 0

  const billingCycleItems: DropdownItem[] = [
    { id: PaymentSchedule.Annual, title: 'Annual', selected: selectedSchedule === PaymentSchedule.Annual },
    { id: PaymentSchedule.Monthly, title: 'Monthly', selected: selectedSchedule === PaymentSchedule.Monthly },
  ]

  const handleBillingCycleChange = (selectedItems: DropdownItem[]) => {
    const newItem = selectedItems[0]
    const next = newItem?.id === PaymentSchedule.Monthly ? PaymentSchedule.Monthly : PaymentSchedule.Annual
    setSelectedSchedule(next)
  }

  const handleContinue = () => {
    if (checkoutInfo.url) {
      trackEvent?.(EventName.BillingInfoSubmitted, { plan_type: planType, schedule: selectedSchedule })
      trackEvent?.(EventName.CheckoutStarted, {
        source: 'billing_modal',
        platform: PLATFORM_ADMIN_PORTAL,
        isTeam: true,
        plan_id: planType,
        schedule: selectedSchedule,
        variant,
      })
      window.location.href = checkoutInfo.url
    }
  }

  const billedInDescription = useMemo(() => {
    const seatLabel = seatsCount !== 1 ? 'seats' : 'seat'
    if (checkoutInfo.trialDays === 0) {
      return `Billed today for ${seatsCount} ${seatLabel}`
    }
    const dayLabel = checkoutInfo.trialDays !== 1 ? 'days' : 'day'
    return `Billed in ${checkoutInfo.trialDays} ${dayLabel} for ${seatsCount} ${seatLabel}`
  }, [checkoutInfo.trialDays, seatsCount])

  return (
    <DialogWrapper
      isOpen={isOpen}
      title="Add billing information"
      subtitle={
        checkoutInfo.trialDays === 0
          ? 'You will be charged immediately for all active users and pending invites.'
          : "Pay $0 today. You will be charged when the first member on your team's trial ends."
      }
      close={onClose}
      minWidth={520}
    >
      <div className={styles.content}>
        <div className={styles.billingCycleRow}>
          <span>Billing Cycle</span>
          <div className={styles.selectWrapper}>
            <Dropdown
              items={billingCycleItems}
              setItems={() => {}}
              onSelect={handleBillingCycleChange}
              className={styles.select}
            />
          </div>
        </div>

        <div className={styles.row}>
          <span>Subtotal</span>
          <span className={styles.amount}>{isLoading || subtotal === undefined ? '—' : formatPrice(subtotal)}</span>
        </div>

        <div className={styles.billedOnSection}>
          <div className={styles.billedOnHeader}>
            <span className={styles.billedOnLabel}>{isLoading ? 'Loading…' : billedInDescription}</span>
            <span className={styles.amount}>
              {isLoading || subtotal === undefined || unitPrice === undefined
                ? '—'
                : `${formatPrice(subtotal)}${seatsCount > 1 ? ` (${formatPrice(unitPrice)}/seat)` : ''}`}
            </span>
          </div>
          <p className={styles.billedOnDescription}>
            {isLoading
              ? ' '
              : checkoutInfo.trialDays === 0
                ? seatsCount > 1
                  ? 'This includes all active team members and pending invitations.'
                  : 'This includes your active team member and any pending invitations.'
                : seatsCount > 1
                  ? "This is the date when the first members' trials end on your team."
                  : "This is the date when the first member's trial ends on your team."}
          </p>
        </div>

        <div className={styles.divider} />

        <div className={styles.row}>
          Billed Today
          <span className={styles.billedTodayAmount}>{isLoading ? '—' : formatPrice(billedToday)}</span>
        </div>

        <Button
          title={isLoading ? 'Loading…' : 'Continue to Payment'}
          onClick={handleContinue}
          type="dark"
          isDisabled={isLoading || !checkoutInfo.url}
        />

        {variant && <PurchaseTerms className={styles.purchaseTerms} />}

        {error && <p className={styles.error}>{error}</p>}

        <p className={styles.footerText}>
          {checkoutInfo.trialDays === 0
            ? `You will be billed ${selectedSchedule === PaymentSchedule.Monthly ? 'on your renewal cycle' : 'monthly'} for each new team member that joins.`
            : `You will be billed ${selectedSchedule === PaymentSchedule.Monthly ? 'on your renewal cycle' : 'monthly,'} for each new team member after their trial ends.`}
        </p>
      </div>
    </DialogWrapper>
  )
}

export default BillingInfoModal
